import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, Rocket } from 'lucide-react'
import randomString from 'random-string'
import { useMemo, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import {
  adjectives,
  animals,
  colors,
  uniqueNamesGenerator,
} from 'unique-names-generator'
import { z } from 'zod/v4'
import { useClusters } from '@/components/form/location-selector/use-clusters'
import { COMPANY_LEVEL_VALUE } from '@/components/form/project-select-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import { H3 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { databaseResourceTypeMap } from '@/config/database-resource-types'
import { getDatabaseVersions } from '@/config/database-versions'
import { gcpClusterMap } from '@/config/gcp-clusters'
import {
  useCompanyPath,
  useCreateFormProjectSelection,
  useIdCompany,
  useMixpanel,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { isOciDumpBackupDatabase } from '@/routes/database/tabs/backups/utils'
import {
  type CreateDatabaseFormValues,
  schema,
  withSupportedVersionSchema,
} from '@/routes/databases/create-database/form/schema'
import { DbData } from '@/routes/databases/create-database/form/sections/db-data'
import { ExternalBackups } from '@/routes/databases/create-database/form/sections/external-backups'
import {
  EXTERNAL_BACKUP_RETENTION_DAYS,
  EXTERNAL_BACKUP_SCHEDULE,
  getSuggestedObjectStorageName,
} from '@/routes/databases/create-database/form/sections/external-backups/constants'
import { Location } from '@/routes/databases/create-database/form/sections/location'
import { Name } from '@/routes/databases/create-database/form/sections/name'
import { Overview } from '@/routes/databases/create-database/form/sections/overview'
import { Project } from '@/routes/databases/create-database/form/sections/project'
import { Resource } from '@/routes/databases/create-database/form/sections/resource'
import { Type } from '@/routes/databases/create-database/form/sections/type'
import {
  CREATE_DATABASE_MUTATION,
  CREATE_DATABASE_QUERY,
} from '@/routes/databases/create-database/gql'
import type {
  CreateDatabaseInfoQuery,
  CreateDatabaseInfoQueryVariables,
  CreateDatabaseMutation,
  CreateDatabaseMutationVariables,
} from '@/routes/databases/create-database/gql.types.gen'

export const CreateDatabaseForm = () => {
  const { idCompany } = useIdCompany()
  const { rbac, totalServices, rolesInCompany } = useRBAC()
  const { navigateToPath } = useCompanyPath()
  const { track } = useMixpanel()
  const [isPending, startTransition] = useTransition()
  const [searchParams] = useSearchParams()

  const idProjectFromUrl = searchParams.get('idProject')

  const { data } = useSuspenseQuery<
    CreateDatabaseInfoQuery,
    CreateDatabaseInfoQueryVariables
  >(CREATE_DATABASE_QUERY, { variables: { idCompany } })

  const { clusters } = useClusters()

  const providerByIdCluster = useMemo(
    () =>
      Object.fromEntries(
        clusters.map((cluster) => [cluster.id, cluster.provider])
      ),
    [clusters]
  )

  const schemaWithSupportedVersion = useMemo(
    () => withSupportedVersionSchema(providerByIdCluster),
    [providerByIdCluster]
  )

  const [createDatabase] = useMutation<
    CreateDatabaseMutation,
    CreateDatabaseMutationVariables
  >(CREATE_DATABASE_MUTATION)

  // Filter projects by RBAC permission
  const projects = useMemo(() => {
    if (totalServices.totalProjects === 0) {
      return []
    }
    return (data?.company?.projects?.items ?? []).filter((project) =>
      rbac.company(idCompany).project(project.id).databases.create.can()
    )
  }, [
    data?.company?.projects?.items,
    idCompany,
    rbac,
    totalServices.totalProjects,
  ])

  const shouldShowProjectField = projects.length > 0

  // Company-level users can create databases at the company level (not just in projects)
  // They should be allowed to deselect a project to create a company-level database
  const isCompanyLevelUser = rbac.company(idCompany).databases.create.can()
  const { initialIdProject, isProjectOptional, schemaWithProjectRequirement } =
    useCreateFormProjectSelection({
      schema: schemaWithSupportedVersion,
      rolesInCompany,
      isCompanyLevelUser,
      idProjectFromUrl,
      projects,
    })
  const hasCreateAccess = isCompanyLevelUser || projects.length > 0

  const name = uniqueNamesGenerator({
    dictionaries: [adjectives, colors, animals],
    separator: '-',
    style: 'lowerCase',
    length: 3,
  }).replace(/\s/g, '-')

  const user = uniqueNamesGenerator({
    dictionaries: [adjectives, colors, animals],
    separator: '-',
    length: 3,
    style: 'lowerCase',
  })
    .replace(/\s/g, '-')
    .slice(0, 32)
    .replace(/-$/, '')

  const form = useForm<CreateDatabaseFormValues>({
    resolver: zodResolver(schemaWithProjectRequirement),
    shouldUnregister: false,
    criteriaMode: 'all',
    defaultValues: {
      idCompany,
      idProject: initialIdProject,
      type: 'postgresql',
      version: getDatabaseVersions({ type: 'postgresql' })[0] ?? '',
      idCluster: '',
      displayName: name,
      idResourceType: '',
      data: {
        dbPassword: randomString({
          length: 12,
          numeric: false,
        }).toLowerCase(),
        dbName: name,
        dbUser: user,
      },
      extensions: {
        enablePostgis: false,
        enableVector: false,
      },
      externalBackup: {
        isEnabled: true,
        storageMode: 'new',
        idObjectStorage: '',
        objectStorageDisplayName: getSuggestedObjectStorageName(name),
      },
    },
  })

  const { trigger } = form
  const provider = providerByIdCluster[form.watch('idCluster')]
  const selectedType = form.watch('type')
  const selectedIdProject = form.watch('idProject')

  const canCreateObjectStorage =
    selectedIdProject && selectedIdProject !== COMPANY_LEVEL_VALUE
      ? rbac
          .company(idCompany)
          .project(selectedIdProject)
          .objectStorages.create.can()
      : rbac.company(idCompany).objectStorages.create.can()
  const hasExternalBackups =
    canCreateObjectStorage &&
    isOciDumpBackupDatabase({
      type: selectedType,
      cluster: provider ? { provider } : null,
    })

  const onSubmit = async (values: z.infer<typeof schema>) => {
    const isValid = await trigger()
    if (isPending || !isValid) {
      return
    }
    startTransition(async () => {
      try {
        // Convert COMPANY_LEVEL_VALUE to undefined for API
        const idProject =
          values.idProject === COMPANY_LEVEL_VALUE
            ? undefined
            : values.idProject

        const { externalBackup } = values
        const isExternalBackupEnabled =
          hasExternalBackups && externalBackup.isEnabled
        const isExistingObjectStorage =
          externalBackup.storageMode === 'existing'

        const res = await createDatabase({
          variables: {
            idCompany,
            idProject,
            idResourceType: values.idResourceType,
            displayName: values.displayName,
            idCluster: values.idCluster,
            version: values.version,
            type: values.type,
            data: values.data,
            extensions: values.extensions,
            externalBackup: isExternalBackupEnabled
              ? {
                  isEnabled: true,
                  idObjectStorage: isExistingObjectStorage
                    ? externalBackup.idObjectStorage
                    : null,
                  objectStorageDisplayName: isExistingObjectStorage
                    ? null
                    : externalBackup.objectStorageDisplayName.trim(),
                  schedule: EXTERNAL_BACKUP_SCHEDULE,
                  retentionDays: EXTERNAL_BACKUP_RETENTION_DAYS,
                }
              : null,
          },
        })
        toast(
          <ToasterContent variant="success">
            Database created successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        track('Database Created', {
          idDatabase: res.data?.createDatabase?.id,
          type: values.type,
          version: values.version,
          provider: providerByIdCluster[values.idCluster],
          location: gcpClusterMap[values.idCluster] ?? values.idCluster,
          resourceType:
            databaseResourceTypeMap[values.idResourceType] ??
            values.idResourceType,
          isExternalBackupEnabled,
          externalBackupStorageMode: isExternalBackupEnabled
            ? externalBackup.storageMode
            : undefined,
        })

        navigateToPath({
          pathname: '/database/:databaseName/overview',
          variables: { databaseName: res.data?.createDatabase?.name ?? '' },
        })
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to create database
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <Form {...form}>
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-[2fr_1fr] pb-25">
        <div className="flex flex-col">
          <Card>
            <CardContent className="p-0">
              <H3 className="mb-6 px-6">Database details</H3>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="px-6 space-y-6">
                  <Type />
                  <DbData provider={provider} />
                </div>
                <Separator />
                <div className="px-6 space-y-6">
                  <Name />
                  {shouldShowProjectField ? (
                    <Project
                      projects={projects}
                      isAllowedToDeselect={isProjectOptional}
                    />
                  ) : null}
                  <Location />
                  <Resource />
                </div>
                {hasExternalBackups ? (
                  <>
                    <Separator />
                    <div className="px-6">
                      <ExternalBackups />
                    </div>
                  </>
                ) : null}
                <div className="px-6">
                  <Button disabled={!hasCreateAccess || isPending}>
                    {isPending ? (
                      <Loader className="animate-spin" />
                    ) : (
                      <Rocket />
                    )}
                    Create database
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col gap-4">
          <Overview />
        </div>
      </div>
    </Form>
  )
}
